import React from 'react';
import Skeleton from 'react-loading-skeleton';

import { Icon, IconTypes } from '@eika/icon';

import { currencyFormatter } from '../../components/shared/numberFormat';
import { useConfig } from '../../defaultConfig';
import { useAllFuturePayments } from '../../fetchHooks/useFuturePayments';
import { useAllLoanPayments } from '../../fetchHooks/useLoans';
import useTranslation, { format } from '../../hooks/useTranslation';
import * as FuturePaymentService from '../../services/core/FuturePaymentService';

import './FuturePaymentsCard.scss';

const FuturePaymentCard: React.FC = () => {
    const translated = useTranslation();
    const { futurePaymentsListLink, featureToggles } = useConfig();

    const futurePaymentsQuery = useAllFuturePayments();
    const { data: loanPayments = [] } = useAllLoanPayments(featureToggles.enableFakedLoanFuturePayment);

    if (futurePaymentsQuery.status === 'error') {
        return null;
    }

    if (futurePaymentsQuery.status === 'pending' || futurePaymentsQuery.fetchStatus === 'fetching') {
        return (
            <article className="futurepayments-frontpage-card skeleton">
                <Skeleton className="typography-subtitle-bold" height={20} width={160} />
                <Skeleton className="futurepayments-frontpage-card__link" height={65} width="100%" />
            </article>
        );
    }

    if (futurePaymentsQuery.data === undefined) {
        return null;
    }

    const futurePayments = FuturePaymentService.extendWithFakedLoans(futurePaymentsQuery.data, loanPayments);
    const nextMonthPayments = FuturePaymentService.filterNextMonthPayments(futurePayments);
    const total = FuturePaymentService.getTotalResult(nextMonthPayments);

    if (total.numberOfFuturePayments === 0) {
        return null;
    }

    return (
        <article className="futurepayments-frontpage-card">
            <span className="typography-subtitle-bold">{translated.futurePaymentsList.title}</span>
            <a
                className="futurepayments-frontpage-card__link"
                href={futurePaymentsListLink.url}
                data-testid="all-future-payments-link"
            >
                <span className="typography-small">
                    {format(
                        translated.futurePaymentsList.paymentsLeftThisMonth,
                        total.numberOfFuturePayments,
                        total.numberOfFuturePayments === 1
                            ? translated.futurePaymentsList.payment
                            : translated.futurePaymentsList.payments,
                    )}
                </span>
                <span className="amount">
                    <span>{currencyFormatter(total?.totalAmount, 'NOK', 0)}</span>
                    <Icon type={IconTypes.PIL_HOEYRE} />
                </span>
            </a>
        </article>
    );
};

export default FuturePaymentCard;
